import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Avatar,
  Button,
  useTheme,
  alpha,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Schedule as ScheduleIcon,
  Work as WorkIcon,
  Description as DocumentIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import InterviewProgressSteps from './InterviewProgressSteps';
import ViewMeetingModal from './ViewMeetingModal';

interface Application {
  id: string;
  title?: string;
  letter?: string;
  status: string;
  ai_application?: boolean; // Indicates if this application was generated by AI
  created_at: string;
  updated_at: string;
  product?: {
    id: string;
    name: string;
    images?: string[];
    company_id?: string;
  };
  applicant?: {
    id: string;
    alias?: string;
    first_name?: string;
    last_name?: string;
  };
  meeting?: {
    id: string;
    interviewType: 'in_person' | 'video' | 'phone';
    date: string;
    time: string;
    timezone: string;
    location?: string;
    videoLink?: string;
    phoneNumber?: string;
    notes?: string;
    status?: 'scheduled' | 'completed' | 'cancelled';
    createdAt?: string;
    updatedAt?: string;
  };
}

interface AcceptedApplicationsListProps {
  applications: Application[];
  onViewApplication?: (application: Application) => void;
  loading?: boolean;
}

const AcceptedApplicationsList: React.FC<AcceptedApplicationsListProps> = ({
  applications,
  onViewApplication,
  loading = false,
}) => {
  const theme = useTheme();
  const [viewMeetingOpenForId, setViewMeetingOpenForId] = useState<string | null>(null);

  // Interview steps data - shows actual progress based on meeting status
  const getInterviewSteps = (application: Application) => {
    const hasMeeting = !!application.meeting;
    const meetingType = application.meeting?.interviewType;
    
    return [
      {
        id: 'initial-review',
        label: 'Initial Review',
        description: 'Application reviewed and accepted',
        completed: true,
        active: false,
      },
      {
        id: 'interview-scheduling',
        label: hasMeeting ? `${meetingType === 'video' ? 'Video' : meetingType === 'phone' ? 'Phone' : 'In-Person'} Interview` : 'Schedule Interview',
        description: hasMeeting 
          ? `${meetingType === 'video' ? 'Video call' : meetingType === 'phone' ? 'Phone call' : 'In-person meeting'} scheduled`
          : 'Schedule the first interview with the candidate',
        completed: hasMeeting,
        active: !hasMeeting,
        meetingData: application.meeting,
      },
      {
        id: 'technical-interview',
        label: 'Technical Interview',
        description: hasMeeting ? 'Technical skills assessment' : 'Schedule technical assessment',
        completed: false,
        active: false,
      },
      {
        id: 'final-interview',
        label: 'Final Interview',
        description: hasMeeting ? 'Final round with hiring manager' : 'Schedule final interview',
        completed: false,
        active: false,
      },
      {
        id: 'offer-decision',
        label: 'Offer Decision',
        description: 'Final hiring decision',
        completed: false,
        active: false,
      },
    ];
  };

  // Filter only accepted applications
  const acceptedApplications = applications.filter(app => app.status === 'accepted');

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {[1, 2, 3].map((i) => (
          <Card key={i} sx={{ borderRadius: 3, boxShadow: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ width: 60, height: 60, borderRadius: 2, bgcolor: 'grey.200' }} />
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ width: '60%', height: 24, bgcolor: 'grey.200', borderRadius: 1, mb: 1 }} />
                  <Box sx={{ width: '40%', height: 20, bgcolor: 'grey.200', borderRadius: 1 }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>
    );
  }

  if (acceptedApplications.length === 0) {
    return (
      <Card 
        sx={{ 
          borderRadius: 3, 
          boxShadow: 2, 
          textAlign: 'center', 
          py: 6,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
        }}
      >
        <CardContent>
          <CheckIcon 
            sx={{ 
              fontSize: 80, 
              color: 'success.main', 
              mb: 2,
              opacity: 0.7
            }} 
          />
          <Typography variant="h5" fontWeight="bold" color="text.primary" sx={{ mb: 1 }}>
            No Accepted Applications Yet
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
            Your accepted job applications will appear here. Keep applying and stay positive! 🎉
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {acceptedApplications.map((application, index) => (
        <motion.div
          key={application.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: index * 0.1 }}
        >
          <Card 
            sx={{ 
              borderRadius: 3, 
              boxShadow: 2,
              border: `2px solid ${alpha(theme.palette.success.main, 0.2)}`,
              background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.02)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
              '&:hover': { 
                boxShadow: 4,
                transform: 'translateY(-2px)',
                transition: 'all 0.3s ease-in-out'
              }
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
                {/* Company/Job Avatar */}
                <Avatar
                  sx={{ 
                    width: 80, 
                    height: 80, 
                    borderRadius: 2,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    fontSize: '2rem'
                  }}
                >
                  {application.product?.images?.[0] ? (
                    <Box
                      component="img"
                      src={application.product.images[0]}
                      alt={application.product.name}
                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <BusinessIcon sx={{ fontSize: '2rem' }} />
                  )}
                </Avatar>

                {/* Application Details */}
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                      <Typography variant="h5" fontWeight="bold" color="text.primary" sx={{ mb: 0.5 }}>
                        {application.product?.name || application.title || 'Job Application'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Application submitted on {new Date(application.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip
                        icon={<CheckIcon />}
                        label="Accepted"
                        color="success"
                        sx={{ 
                          fontWeight: 'bold',
                          px: 1,
                          '& .MuiChip-icon': {
                            fontSize: '1.2rem'
                          }
                        }}
                      />
                      {application.ai_application && (
                        <Chip 
                          label="AI Generated" 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                          sx={{ 
                            fontWeight: 'bold',
                            borderColor: 'primary.main',
                            color: 'primary.main',
                            '& .MuiChip-label': {
                              fontSize: '0.75rem'
                            }
                          }}
                        />
                      )}
                    </Box>
                  </Box>

                  {/* Application Info */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <WorkIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                      <Typography variant="body2" color="text.secondary">
                        Job Position
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <ScheduleIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                      <Typography variant="body2" color="text.secondary">
                        {new Date(application.updated_at).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Cover Letter Preview */}
                  {application.letter && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ 
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        lineHeight: 1.4
                      }}>
                        "{application.letter.substring(0, 150)}{application.letter.length > 150 ? '...' : ''}"
                      </Typography>
                    </Box>
                  )}

                  {/* Action Buttons */}
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      startIcon={<ViewIcon />}
                      onClick={() => application.meeting ? setViewMeetingOpenForId(application.id) : onViewApplication?.(application)}
                      sx={{
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                        '&:hover': {
                          background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
                        },
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 'bold',
                        px: 3
                      }}
                    >
                      {application.meeting ? 'View Meeting' : 'View Details'}
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<DocumentIcon />}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 'bold',
                        px: 3,
                        borderColor: 'success.main',
                        color: 'success.main',
                        '&:hover': {
                          borderColor: 'success.dark',
                          backgroundColor: alpha(theme.palette.success.main, 0.1)
                        }
                      }}
                    >
                      Documents
                    </Button>
                  </Box>

                  {/* Interview Progress Steps */}
                  <Box sx={{ mt: 3 }}>
                    <InterviewProgressSteps
                      steps={getInterviewSteps(application)}
                      currentStep={getInterviewSteps(application).findIndex(step => step.active)}
                      variant="compact"
                      onStepClick={(stepId, stepIndex) => {
                        const step = getInterviewSteps(application)[stepIndex];
                        if (step.meetingData) {
                          setViewMeetingOpenForId(application.id);
                        } else {
                          console.log('Step clicked:', stepId, stepIndex);
                          // Handle step click - could open a modal or navigate to step details
                        }
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
          {viewMeetingOpenForId === application.id && (
            <ViewMeetingModal
              open
              onClose={() => setViewMeetingOpenForId(null)}
              meeting={application.meeting}
              application={application}
            />
          )}
        </motion.div>
      ))}
    </Box>
  );
};

export default AcceptedApplicationsList;
